import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type DefaultValues, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { describeError } from './error-text';

export interface AdminFormOptions<TSchema extends z.ZodType> {
  schema: TSchema;
  defaultValues: DefaultValues<z.input<TSchema>>;
}

export interface AdminForm<TSchema extends z.ZodType> extends UseFormReturn<
  z.input<TSchema>,
  unknown,
  z.output<TSchema>
> {
  /**
   * Serverdan kelgan xatoni formaga bog'laydi.
   *
   * Server maydon nomini aytmaydi (`{code, message}`), shuning uchun xato butun formaga
   * yoziladi — `root` ga. Uni maydonga taxmin bilan bog'lash yomonroq bo'lardi: noto'g'ri
   * maydon ostidagi qizil matn operatorni tuzatib bo'lmaydigan narsani tuzatishga majbur
   * qilardi.
   */
  applyServerError: (error: unknown) => void;
  /** `root` xatosining matni — forma tugmalari ustida chiziladi. */
  serverError: string | undefined;
}

/**
 * Admin panelidagi formalarning yagona asosi.
 *
 * **Nega `react-hook-form` + zod.** Ilgari har bir forma qo'lda `useState` bilan yozilardi va
 * har biri validatsiyani o'zicha hal qilardi: biri bo'sh maydonni yubormasdi, ikkinchisi
 * yuborardi va serverning 400 ini kutardi, uchinchisi umuman tekshirmasdi. Muhimi esa boshqa
 * narsa: tekshiruv qoidasi shartnomadan (`@qaytim/shared` dagi zod sxemasi) UZILGAN edi, ya'ni
 * server qoidani o'zgartirsa, forma buni bilmasdi. Endi bitta sxema ikkalasini ham boshqaradi.
 *
 * **Nega `mode: 'onTouched'`.** Har bosishda tekshirish (`onChange`) foydalanuvchi hali
 * yozayotgan maydon ostida qizil matn chiqarardi; faqat yuborishda tekshirish esa xatoni
 * eng kech paytda ko'rsatardi. Maydondan chiqqanda tekshirish — ikkisining o'rtasi.
 */
export function useAdminForm<TSchema extends z.ZodType<FieldValues, z.ZodTypeDef, FieldValues>>({
  schema,
  defaultValues,
}: AdminFormOptions<TSchema>): AdminForm<TSchema> {
  const form = useForm<z.input<TSchema>, unknown, z.output<TSchema>>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onTouched',
  });

  const applyServerError = (error: unknown) => {
    form.setError('root', { type: 'server', message: describeError(error) });
  };

  return {
    ...form,
    applyServerError,
    serverError: form.formState.errors.root?.message,
  };
}

/**
 * Ixtiyoriy raqamli maydon — matn kiritishdan.
 *
 * `<input type="number">` bo'sh bo'lganda `''` qaytaradi, `valueAsNumber` esa `NaN`. Ikkalasi
 * ham shartnomadagi `z.number().optional()` ga tushmaydi va operator "Raqam kiriting" degan
 * xatoni hech narsa yozmagan maydon ostida ko'rardi. Bu yerda bo'sh matn "BERILMAGAN" deb
 * o'qiladi — nol emas, chunki nol pozitsiya haqiqiy qiymat.
 */
export const optionalNumberField = z
  .union([z.string(), z.number()])
  .transform((value) => (String(value).trim() === '' ? undefined : Number(value)))
  .pipe(z.number().int().nonnegative().optional());

/**
 * Majburiy raqamli maydon — matn kiritishdan, shartnomaning O'Z qoidasi bilan.
 *
 * `z.coerce.number()` bu yerda ishlamaydi: uning KIRISH turi ham `number`, ya'ni forma bo'sh
 * satr bilan boshlana olmasdi. Bo'sh maydon `NaN` ga o'giriladi va qoida uni rad etadi —
 * "Raqam kiriting" degan xabar bilan (`zod-messages.ts`).
 */
export function numberField<TRule extends z.ZodTypeAny>(rule: TRule) {
  return z
    .union([z.string(), z.number()])
    .transform((value) => (String(value).trim() === '' ? Number.NaN : Number(value)))
    .pipe(rule);
}

/** Matn maydoni: chetlaridagi bo'shliq kesiladi, keyin shartnomaning O'Z qoidasi qo'llanadi. */
export function trimmed(rule: z.ZodString) {
  return z
    .string()
    .transform((value) => value.trim())
    .pipe(rule);
}
